import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuthStore } from '../lib/auth.store';
import { PageHeader } from '../components/PageHeader';
import { LivePulse } from '../ui/LivePulse';
import { TriDot } from '../ui/TriDot';
import { Pill } from '../ui/Pill';
import { Sparkline } from '../ui/Sparkline';
import { BarChart } from '../ui/BarChart';
import { Icon } from '../ui/Icons';
import type {
  Agent,
  Conversation,
  Lead,
  UsageSeries,
} from '../lib/types';

const WINDOW_DAYS = 14;

type LeadTone = 'new' | 'contacted' | 'qualified' | 'won' | 'lost';
const leadTone = (status: string): LeadTone =>
  (status.toLowerCase() as LeadTone) ?? 'new';

export const Dashboard = () => {
  const user = useAuthStore((s) => s.user);

  const agentsQ = useQuery<{ agents: Agent[] }>({
    queryKey: ['agents'],
    queryFn: async () => (await api.get('/api/tenant/agents')).data,
  });
  const seriesQ = useQuery<UsageSeries>({
    queryKey: ['usage', 'series', WINDOW_DAYS],
    queryFn: async () =>
      (await api.get(`/api/tenant/usage/series?days=${WINDOW_DAYS}`)).data,
  });
  const convsQ = useQuery<{ conversations: Conversation[] }>({
    queryKey: ['conversations', 'preview'],
    queryFn: async () =>
      (await api.get('/api/tenant/conversations?limit=10')).data,
  });
  const leadsQ = useQuery<{ leads: Lead[] }>({
    queryKey: ['leads', 'preview'],
    queryFn: async () => (await api.get('/api/tenant/leads?limit=10')).data,
  });

  const series = seriesQ.data;
  const agent = agentsQ.data?.agents[0];
  const agentLive = agent?.status === 'ACTIVE';
  const firstName = user?.name?.split(' ')[0];

  const convCount = series?.thisPeriod.conversations ?? 0;
  const leadsCount = series?.thisPeriod.leads ?? 0;
  const replyMs = series?.thisPeriod.avgReplyMs ?? null;
  const spend = parseFloat(series?.thisPeriod.costUsd ?? '0');
  const costPerChat = convCount > 0 ? spend / convCount : 0;

  const convDelta = pctDelta(convCount, series?.previousPeriod.conversations ?? 0);
  const leadDelta = pctDelta(leadsCount, series?.previousPeriod.leads ?? 0);
  const replyDelta = replyMs != null && series?.previousPeriod.avgReplyMs != null
    ? pctDelta(replyMs, series.previousPeriod.avgReplyMs)
    : null;
  const spendDelta = pctDelta(spend, parseFloat(series?.previousPeriod.costUsd ?? '0'));

  const convSpark = (series?.buckets ?? []).map((b) => b.conversations);
  const leadSpark = (series?.buckets ?? []).map((b) => b.leads);
  // For reply time + cost, zero-bucket noise hurts readability; fall back to a
  // flat baseline if there's no data in the window.
  const replySpark = (series?.buckets ?? []).map((b) => b.avgReplyMs ?? 0);
  const spendSpark = (series?.buckets ?? []).map((b) => parseFloat(b.costUsd));

  const barData = useMemo(() => {
    if (!series) return [];
    const last12 = series.buckets.slice(-12);
    const maxConv = Math.max(1, ...last12.map((b) => b.conversations));
    return last12.map((b) => ({
      label: shortDay(b.date),
      value: b.conversations,
      highlight: b.conversations >= maxConv * 0.75 && b.conversations > 0,
    }));
  }, [series]);

  const activity = useMemo(
    () => composeActivity(convsQ.data?.conversations, leadsQ.data?.leads),
    [convsQ.data, leadsQ.data],
  );

  const subtitle = agent
    ? `${agent.name} has handled ${convCount} ${convCount === 1 ? 'chat' : 'chats'} in the last ${WINDOW_DAYS} days.`
    : 'Your agent is being set up.';

  return (
    <div>
      <PageHeader
        hero
        title={
          <>
            Hi {firstName ?? 'there'},{' '}
            <span className="text-ink-subtle italic">here's today.</span>
          </>
        }
        subtitle={subtitle}
        actions={
          <>
            {agentLive && <LivePulse label="Agent live" />}
            <button type="button" className="btn-ghost" disabled>
              <Icon.Cal size={14} />
              Last {WINDOW_DAYS} days
            </button>
          </>
        }
      />

      <div className="px-10 pb-10 flex flex-col gap-[18px]">
        {/* Stat strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5">
          <StatCard
            label="Conversations"
            value={convCount.toLocaleString()}
            delta={convDelta}
            points={convSpark}
            loading={seriesQ.isLoading}
          />
          <StatCard
            label="New leads"
            value={String(leadsCount)}
            delta={leadDelta}
            points={leadSpark}
            accent
            loading={seriesQ.isLoading}
          />
          <StatCard
            label="Reply time"
            value={replyMs != null ? formatMs(replyMs) : '—'}
            sub={
              replyMs != null
                ? 'mean across assistant turns'
                : 'no measured turns yet'
            }
            delta={replyDelta != null ? invertDelta(replyDelta) : undefined}
            points={replySpark}
            color="#10B981"
            loading={seriesQ.isLoading}
          />
          <StatCard
            label="Spend"
            value={`$${spend.toFixed(2)}`}
            sub={costPerChat > 0 ? `$${costPerChat.toFixed(3)} / chat` : 'no chats yet'}
            delta={spendDelta}
            points={spendSpark}
            color="#9CA3AF"
            loading={seriesQ.isLoading}
          />
        </div>

        {/* Chart + quick actions */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-3.5">
          <section className="card overflow-hidden">
            <div className="px-5 pt-[18px] pb-1 flex items-center justify-between gap-4">
              <div>
                <div className="text-[13.5px] font-semibold text-ink">Conversations &amp; leads</div>
                <div className="text-[11.5px] text-ink-subtle mt-0.5">
                  Last 12 days · UTC buckets
                </div>
              </div>
              <div className="flex gap-3.5 text-[11px] text-ink-muted">
                <Legend color="#6366F1" label="Conversations" />
                <Legend color="#D1D5DB" label="Leads (overlay)" />
              </div>
            </div>
            <div className="px-3 pb-3.5">
              {seriesQ.isLoading ? (
                <div className="h-[170px] mx-3 bg-bg rounded animate-pulse" />
              ) : barData.length === 0 ? (
                <div className="h-[170px] flex items-center justify-center text-[12.5px] text-ink-subtle">
                  No activity in this window yet.
                </div>
              ) : (
                <BarChart data={barData} width={580} height={170} />
              )}
            </div>
          </section>

          <section className="card overflow-hidden">
            <div className="px-5 pt-[18px] pb-3 flex items-center justify-between">
              <div className="text-[13.5px] font-semibold text-ink">Quick actions</div>
              <TriDot size={4} gap={3} />
            </div>
            <div className="px-3.5 pb-3.5 flex flex-col gap-2">
              <QuickAction to="/agent" icon={Icon.Sparkle} title="Tune your agent" sub="Edit persona & welcome message" />
              <QuickAction to="/knowledge" icon={Icon.Book} title="Add knowledge" sub="Upload a PDF or paste text" />
              <QuickAction to="/agent" icon={Icon.Copy} title="Grab embed snippet" sub="Drop the script on your site" />
              <QuickAction to="/conversations" icon={Icon.Chat} title="Review conversations" sub="See what visitors are asking" />
            </div>
          </section>
        </div>

        {/* Activity feed + recent leads */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-3.5">
          <section className="card overflow-hidden">
            <CardHeader
              title="Live activity"
              right={
                <Link to="/conversations" className="text-[11.5px] text-accent hover:underline">
                  View all conversations →
                </Link>
              }
            />
            {convsQ.isLoading || leadsQ.isLoading ? (
              <Skeleton />
            ) : convsQ.error || leadsQ.error ? (
              <Err msg={errorMessage(convsQ.error ?? leadsQ.error)} />
            ) : activity.length === 0 ? (
              <Empty>Nothing yet — once your agent starts chatting it shows up here.</Empty>
            ) : (
              <ul className="list-none m-0 px-1 pb-2">
                {activity.map((a, i) => (
                  <li
                    key={a.id}
                    className={`flex items-center gap-3 px-[18px] py-[11px] ${i > 0 ? 'border-t border-line' : ''}`}
                  >
                    <span className="w-7 h-7 rounded-lg bg-[#F4F4F7] text-ink-soft flex items-center justify-center shrink-0">
                      <a.Icon size={13} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium text-ink leading-tight truncate">{a.title}</div>
                      <div className="text-[11.5px] text-ink-subtle mt-0.5 leading-snug truncate">{a.sub}</div>
                    </span>
                    {a.pill && <Pill tone={a.pill.tone} dot>{a.pill.label}</Pill>}
                    <span className="text-[11px] text-ink-subtle tabular-nums min-w-[28px] text-right">{a.time}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card overflow-hidden">
            <CardHeader
              title="Recent leads"
              right={
                <Link to="/leads" className="text-[11.5px] text-accent hover:underline">
                  All leads →
                </Link>
              }
            />
            {leadsQ.isLoading ? <Skeleton /> : leadsQ.error ? (
              <Err msg={errorMessage(leadsQ.error)} />
            ) : (leadsQ.data?.leads.length ?? 0) === 0 ? (
              <Empty>No leads captured yet.</Empty>
            ) : (
              <ul className="list-none m-0 px-[18px] pb-3.5">
                {leadsQ.data!.leads.slice(0, 5).map((l, i) => {
                  const display = l.name ?? l.email ?? '(unnamed)';
                  const contact = l.email ?? l.phone ?? '—';
                  return (
                    <li
                      key={l.id}
                      className={`flex items-center gap-2.5 py-2.5 ${i > 0 ? 'border-t border-line' : ''}`}
                    >
                      <div className="w-[26px] h-[26px] rounded-full bg-[#F2F2F5] text-ink-soft flex items-center justify-center text-[10.5px] font-semibold shrink-0">
                        {display.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-medium text-ink leading-tight truncate">{display}</div>
                        <div className="text-[11px] text-ink-subtle mt-px truncate">{contact}</div>
                      </span>
                      <Pill tone={leadTone(l.status)} dot>{l.status.toLowerCase()}</Pill>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

// ---- helpers ----

interface ActivityItem {
  id: string;
  Icon: typeof Icon.Chat;
  title: string;
  sub: string;
  time: string;
  pill?: { tone: 'live' | 'new' | 'qualified' | 'won' | 'lost' | 'contacted' | 'accent'; label: string };
}

const composeActivity = (
  convs: Conversation[] | undefined,
  leads: Lead[] | undefined,
): ActivityItem[] => {
  const items: (ActivityItem & { sortAt: number })[] = [];

  convs?.forEach((c) => {
    const started = new Date(c.startedAt).getTime();
    const ended = c.endedAt ? new Date(c.endedAt).getTime() : null;
    const active = !ended && Date.now() - started < 10 * 60 * 1000;
    items.push({
      id: `conv-${c.id}`,
      Icon: Icon.Chat,
      title: active
        ? 'New chat started'
        : `Chat ended · ${c.messageCount} msg${c.messageCount === 1 ? '' : 's'}`,
      sub: `${c.visitorId.slice(0, 8)}…`,
      time: relTime(ended ?? started),
      pill: active ? { tone: 'live', label: 'active' } : undefined,
      sortAt: ended ?? started,
    });
  });

  leads?.forEach((l) => {
    const at = new Date(l.createdAt).getTime();
    items.push({
      id: `lead-${l.id}`,
      Icon: Icon.Check,
      title: `Lead captured${l.name ? `: ${l.name}` : ''}`,
      sub: l.notes ?? l.email ?? l.phone ?? '—',
      time: relTime(at),
      pill: { tone: leadTone(l.status), label: l.status.toLowerCase() },
      sortAt: at,
    });
  });

  return items.sort((a, b) => b.sortAt - a.sortAt).slice(0, 5);
};

const relTime = (ms: number) => {
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return d === 1 ? 'yest' : `${d}d`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const pctDelta = (current: number, previous: number): DeltaInfo | undefined => {
  if (previous === 0 && current === 0) return undefined;
  if (previous === 0) return { value: `+${current}`, up: true };
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.5) return undefined;
  const rounded = Math.round(pct);
  return { value: `${rounded > 0 ? '+' : ''}${rounded}%`, up: rounded >= 0 };
};

// "Lower is better" cards (reply time, eventually error rate) get this:
const invertDelta = (d: DeltaInfo): DeltaInfo => ({ value: d.value, up: !d.up });

interface DeltaInfo {
  value: string;
  up: boolean;
}

const formatMs = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;
};

const shortDay = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return String(d.getUTCDate());
};

interface StatCardProps {
  label: string;
  value: string;
  delta?: DeltaInfo;
  sub?: string;
  points: number[];
  color?: string;
  accent?: boolean;
  loading?: boolean;
}

const StatCard = ({ label, value, delta, sub, points, color, accent, loading }: StatCardProps) => (
  <div
    className={`rounded-2xl p-4 relative overflow-hidden ${
      accent ? 'bg-gradient-tri text-white border-0' : 'bg-white text-ink border border-line'
    }`}
  >
    <div className="flex justify-between items-start">
      <div
        className={`text-[10.5px] font-medium uppercase tracking-[0.1em] ${
          accent ? 'text-white/85' : 'text-ink-subtle'
        }`}
      >
        {label}
      </div>
      {delta && (
        <span
          className={`text-[10.5px] font-medium rounded-full px-1.5 py-0.5 ${
            accent
              ? 'bg-white/20 text-white'
              : delta.up
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-rose-50 text-rose-700'
          }`}
        >
          {delta.up ? '↑' : '↓'} {delta.value.replace(/^[+-]/, '')}
        </span>
      )}
    </div>
    <div className="font-serif text-[38px] leading-none mt-3.5 tracking-tightest font-normal min-h-[38px]">
      {loading ? <span className="text-ink-subtle">…</span> : value}
    </div>
    {sub && (
      <div className={`text-[11px] mt-1.5 ${accent ? 'text-white/85' : 'text-ink-subtle'}`}>
        {sub}
      </div>
    )}
    {points.length > 1 && (
      <div className={sub ? 'mt-1.5' : 'mt-3'}>
        <Sparkline
          points={points}
          color={accent ? '#FFFFFF' : color ?? '#6366F1'}
          width={180}
          height={28}
        />
      </div>
    )}
  </div>
);

const QuickAction = ({
  to,
  icon: Ico,
  title,
  sub,
}: {
  to: string;
  icon: typeof Icon.Chat;
  title: string;
  sub: string;
}) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-3 py-2.5 rounded-[10px] border border-line bg-white hover:border-line-strong transition-colors"
  >
    <span className="w-[30px] h-[30px] rounded-lg bg-bg text-ink-soft flex items-center justify-center shrink-0">
      <Ico size={14} />
    </span>
    <span className="flex-1 min-w-0">
      <div className="text-[12.5px] font-medium text-ink leading-tight">{title}</div>
      <div className="text-[11px] text-ink-subtle leading-tight mt-0.5">{sub}</div>
    </span>
    <span className="text-ink-subtle">
      <Icon.ArrowUpRight size={14} />
    </span>
  </Link>
);

const CardHeader = ({ title, right }: { title: string; right?: React.ReactNode }) => (
  <div className="px-[18px] pt-4 pb-3.5 border-b border-line flex items-center justify-between">
    <div className="text-[13.5px] font-semibold text-ink">{title}</div>
    {right}
  </div>
);

const Legend = ({ color, label }: { color: string; label: string }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
    {label}
  </span>
);

const Skeleton = () => <div className="h-32 mx-[18px] mb-4 bg-bg rounded animate-pulse" />;
const Empty = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[12.5px] text-ink-subtle py-6 text-center px-4">{children}</div>
);
const Err = ({ msg }: { msg: string }) => (
  <div className="text-[12.5px] text-rose-700 px-[18px] py-4">{msg}</div>
);
